import { MarkdownEditor, ViewMode } from '@efdigital/ll-addon-codemirror';
import { All } from '@efdigital/ll-addon-codemirror/markdown-toolbar-options';
import type { CalendarItem } from '@efdigital/ll-addon-data';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { set } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';

import { EditRecord, type EditRecordSignature } from '../../edit-record.gts';
import { InputDateOnly, InputDateTime } from '../../input-date.gts';
import { PicklistItemPicker } from '../../pickers/picklist-item-picker.gts';

function isAllDay(calendarItem: CalendarItem) {
  return calendarItem.dateType === 'date';
}

function isChecked(value: string, checkedValue: string) {
  return value === checkedValue;
}

export class EditCalendarItem extends Component<
  EditRecordSignature<CalendarItem>
> {
  @service declare store: Store;

  setValue = (property: string, event: Event) => {
    const target = event.target as HTMLInputElement;
    set(this.args.item, property, target.value);
  };

  setChecked = (property: string, event: Event) => {
    const target = event.target as HTMLInputElement;
    set(this.args.item, property, target.checked);
  };

  setMarkdown = (markdown: string) => {
    set(this.args.item, 'markdown', markdown);
  };

  setDateValue = (property: string, value: string) => {
    set(this.args.item, property, value);
  };

  <template>
    <EditRecord
      @editLabel="Edit Calendar Item"
      @item={{@item}}
      @context={{@context}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Event</legend>

          <PicklistItemPicker
            @group="calendar-event-type"
            @label="Event Type"
            @name="eventType"
            @selected={{@item.eventType}}
            @readonly={{isReadOnly}}
            class="mb-3"
          />

          <div class="mb-3">
            <label
              for="{{uniqueId}}-title"
              class="form-control-label required"
            >Title</label>
            <input
              id="{{uniqueId}}-title"
              name="title"
              type="text"
              readonly={{isReadOnly}}
              value={{@item.title}}
              class="form-control"
              placeholder="Event name"
              required
              {{on "change" (fn this.setValue "title")}}
            />
          </div>

          <div class="mb-3">
            <label for="{{uniqueId}}-icon" class="form-control-label">Preview
              Image</label>
            <input
              id="{{uniqueId}}-icon"
              name="icon"
              type="text"
              readonly={{isReadOnly}}
              value={{@item.icon}}
              class="form-control"
              placeholder="Preview Image"
              {{on "change" (fn this.setValue "icon")}}
            />
          </div>

          <div class="mb-3">
            <label for="abstract" class="col-sm-2 control-label">Body</label>
            <MarkdownEditor
              @markdown={{@item.markdown}}
              @toolbar={{All}}
              @mode={{ViewMode.EditorOnly}}
              @onUpdate={{this.setMarkdown}}
              class="ll-editor-markdown"
            >
              <:preview as |markdown|>
                <pre>{{markdown}}</pre>
              </:preview>
            </MarkdownEditor>
          </div>

          <div class="mb-3">
            <label
              for="{{uniqueId}}-location"
              class="form-control-label"
            >Location</label>
            <input
              id="{{uniqueId}}-location"
              name="location"
              type="text"
              readonly={{isReadOnly}}
              value={{@item.location}}
              class="form-control"
              placeholder="Location"
              {{on "change" (fn this.setValue "location")}}
            />
          </div>

          <div class="mb-3">
            <label
              for="{{uniqueId}}-external"
              class="form-control-label"
            >Meeting Link</label>
            <input
              id="{{uniqueId}}-external"
              name="external"
              type="url"
              readonly={{isReadOnly}}
              value={{@item.external}}
              class="form-control"
              placeholder="e.g. Teams meeting link"
              {{on "change" (fn this.setValue "external")}}
            />
          </div>
        </fieldset>

        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Dates</legend>

          <div class="mb-3">
            <label class="form-control-label">Type</label>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                name="dateType"
                id="{{uniqueId}}-dateType-dateTime"
                value="date-time"
                checked={{isChecked @item.dateType "date-time"}}
                {{on "change" (fn this.setValue "dateType")}}
              />
              <label
                class="form-check-label"
                for="{{uniqueId}}-dateType-dateTime"
              >
                Specific time
              </label>
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                name="dateType"
                id="{{uniqueId}}-dateType-date"
                value="date"
                checked={{isChecked @item.dateType "date"}}
                {{on "change" (fn this.setValue "dateType")}}
              />
              <label class="form-check-label" for="{{uniqueId}}-dateType-date">
                All day
              </label>
            </div>
          </div>

          {{#if (isAllDay @item)}}
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label
                    for="{{uniqueId}}-starts-date"
                    class="form-control-label required"
                  >Starts</label>
                  <InputDateOnly
                    id="{{uniqueId}}-starts-date"
                    name="starts-date"
                    class="form-control"
                    readonly={{isReadOnly}}
                    required
                    @value={{@item.starts}}
                    @onChange={{fn this.setDateValue "starts"}}
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label
                    for="{{uniqueId}}-ends-date"
                    class="form-control-label"
                  >Ends</label>
                  <InputDateOnly
                    id="{{uniqueId}}-ends-date"
                    name="ends-date"
                    class="form-control"
                    readonly={{isReadOnly}}
                    @value={{@item.ends}}
                    @onChange={{fn this.setDateValue "ends"}}
                  />
                </div>
              </div>
            </div>
          {{else}}
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label
                    for="{{uniqueId}}-starts-datetime"
                    class="form-control-label"
                  >Starts</label>
                  <InputDateTime
                    id="{{uniqueId}}-starts-datetime"
                    name="starts-datetime"
                    class="form-control"
                    readonly={{isReadOnly}}
                    @value={{@item.starts}}
                    @onChange={{fn this.setDateValue "starts"}}
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label
                    for="{{uniqueId}}-ends-datetime"
                    class="form-control-label"
                  >Ends</label>
                  <InputDateTime
                    id="{{uniqueId}}-ends-datetime"
                    name="ends-datetime"
                    class="form-control"
                    readonly={{isReadOnly}}
                    @value={{@item.ends}}
                    @onChange={{fn this.setDateValue "ends"}}
                  />
                </div>
              </div>
            </div>
          {{/if}}

          <div class="form-check form-switch mb-3">
            <input
              class="form-check-input"
              type="checkbox"
              role="switch"
              id="{{uniqueId}}-cancelled"
              readonly={{isReadOnly}}
              checked={{@item.cancelled}}
              aria-checked={{@item.cancelled}}
              value="true"
              {{on "change" (fn this.setChecked "cancelled")}}
            />
            <label
              class="form-check-label"
              for="{{uniqueId}}-cancelled"
            >Cancelled</label>
          </div>
        </fieldset>

        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Contacts</legend>

          {{! TODO: Add contacts }}
          {{! TODO: Add folders }}
        </fieldset>
      </:form>
    </EditRecord>
  </template>
}
